import type { ReactNode } from "react";
import {
  Check,
  Download,
  Eye,
  Link as LinkIcon,
  MoreHorizontal,
  Pencil,
  Trash2,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@flint/ui";
import { formatDate, paiseToInrShort } from "../../../lib/format.ts";
import {
  avatarClassFor,
  DataTableCard,
  initialsFor,
  type DataTableStat,
} from "../../master-list/index.ts";
import type { BillListRow, BillStatus } from "./types.ts";

export interface BillTableProps {
  rows: BillListRow[];
  selectedIds: Set<string>;
  onToggleSelected: (id: string) => void;
  onToggleSelectAll: (checked: boolean) => void;
  /**
   * Footer 1 — the visible-set stats strip, built by the route (it owns the
   * KPI payload). `[]` hides the strip. @see DataTableCard
   */
  footer?: DataTableStat[];
  /**
   * Footer 2 — the pagination strip (a `<ListPagination>` from the route,
   * wired to its cursor stack). Omit to hide.
   */
  pagination?: ReactNode;
  /**
   * Row action: ask the host to delete this draft. Only renders the menu
   * item on rows with `status === 'draft'`. Host opens a confirm + fires
   * `bills.deleteDraft`. Pass `undefined` (or omit) to hide the action.
   */
  onDeleteDraft?: (row: BillListRow) => void;
  /**
   * Row action: ask the host to void this bill. Renders on rows past the
   * draft stage but not yet paid or already voided. Host opens a confirm
   * + fires `bills.transitionStatus` with `status: 'void'`.
   */
  onVoidBill?: (row: BillListRow) => void;
  /**
   * Disable destructive menu items while a mutation is in flight, mirroring
   * the team-members-table `busy` prop. The menu trigger itself stays
   * clickable so operators can still inspect actions.
   */
  busy?: boolean;
}

export function BillTable({
  rows,
  selectedIds,
  onToggleSelected,
  onToggleSelectAll,
  footer,
  pagination,
  onDeleteDraft,
  onVoidBill,
  busy,
}: BillTableProps) {
  const allSelected = rows.length > 0 && rows.every((row) => selectedIds.has(row.id));
  const someSelected = !allSelected && rows.some((row) => selectedIds.has(row.id));

  return (
    <DataTableCard {...(footer ? { footer } : {})} {...(pagination ? { pagination } : {})}>
      <table className="w-full min-w-[840px] table-fixed text-sm">
          <colgroup>
            <col className="w-12" />
            <col className="w-28" />
            <col />
            <col className="w-28" />
            <col className="w-32" />
            <col className="w-28" />
            <col className="w-36" />
            <col className="w-12" />
          </colgroup>
          <thead className="bg-raised text-xs uppercase tracking-wide text-muted">
            <tr>
              <th className="px-4 py-4">
                <input
                  type="checkbox"
                  aria-label="Select all bills"
                  checked={allSelected}
                  ref={(node) => {
                    if (node) node.indeterminate = someSelected;
                  }}
                  onChange={(event) => onToggleSelectAll(event.target.checked)}
                />
              </th>
              <th className="px-3 py-4 text-left font-medium">Bill #</th>
              <th className="px-4 py-4 text-left font-medium">Vendor</th>
              <th className="px-3 py-4 text-left font-medium">Issued</th>
              <th className="px-3 py-4 text-left font-medium">Due</th>
              <th className="px-3 py-4 text-right font-medium">Amount</th>
              <th className="px-3 py-4 text-left font-medium">Status</th>
              <th className="px-3 py-4">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={8} className="border-t border-line px-4 py-10 text-center">
                  <p className="font-semibold text-ink">No bills match this view</p>
                  <p className="mt-1 text-sm text-muted">
                    Try clearing search, status, vendor, date, or amount filters.
                  </p>
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const selected = selectedIds.has(row.id);
                const displayNumber = billDisplayNumber(row);
                return (
                  <tr
                    key={row.id}
                    className={[
                      "border-t border-line transition-colors",
                      selected ? "bg-sky-50/60 hover:bg-sky-50" : "hover:bg-raised",
                    ].join(" ")}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        aria-label={`Select ${displayNumber}`}
                        checked={selected}
                        onChange={() => onToggleSelected(row.id)}
                      />
                    </td>
                    <td className="px-3 py-3 font-mono font-semibold text-ink-2">
                      <Link
                        to="/procurement/bills/$billId"
                        params={{ billId: row.id }}
                        className="block truncate hover:underline"
                        title={displayNumber}
                      >
                        {displayNumber}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-ink-2">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <span
                          aria-hidden
                          className={`grid h-8 w-8 shrink-0 place-items-center rounded-md text-xs font-bold ${avatarClassFor(row.vendorName)}`}
                        >
                          {initialsFor(row.vendorName)}
                        </span>
                        <div className="min-w-0">
                          <Link
                            to="/procurement/vendors/$vendorId"
                            params={{ vendorId: row.vendorId }}
                            className="block truncate font-semibold text-ink hover:underline"
                            title={row.vendorName}
                          >
                            {row.vendorName}
                          </Link>
                          <p className="truncate text-xs text-muted">{vendorMeta(row)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 font-mono text-muted">{formatDate(row.receivedOn)}</td>
                    <td className="px-3 py-3">
                      <span className="font-mono text-muted">
                        {row.dueOn ? formatDate(row.dueOn) : "Not set"}
                      </span>
                      <p className={`mt-0.5 text-xs ${dueTone(row)}`}>{dueMeta(row)}</p>
                    </td>
                    <td
                      className={[
                        "px-3 py-3 text-right font-mono font-semibold",
                        row.status === "void" ? "text-muted line-through" : "text-ink",
                      ].join(" ")}
                    >
                      {paiseToInrShort(row.totalPaise)}
                    </td>
                    <td className="px-3 py-3">
                      <StatusBadge status={row.status} match={row.threeWayMatch} />
                    </td>
                    <td className="px-2 py-2 text-right">
                      <RowActions
                        row={row}
                        {...(onDeleteDraft ? { onDeleteDraft } : {})}
                        {...(onVoidBill ? { onVoidBill } : {})}
                        {...(busy !== undefined ? { busy } : {})}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
    </DataTableCard>
  );
}

interface RowActionsProps {
  row: BillListRow;
  onDeleteDraft?: (row: BillListRow) => void;
  onVoidBill?: (row: BillListRow) => void;
  busy?: boolean;
}

function RowActions({ row, onDeleteDraft, onVoidBill, busy }: RowActionsProps) {
  // Pick the right destructive verb for this row's status. Drafts get
  // delete (they never reached the ledger); past-draft live bills get
  // void (preserved as history per the append-only contract).
  const isDraft = row.status === "draft";
  const isEditable = row.status === "draft" || row.status === "review";
  const destructive = isDraft
    ? onDeleteDraft
      ? { label: "Delete draft", onSelect: () => onDeleteDraft(row) }
      : null
    : onVoidBill
      ? { label: "Void bill", onSelect: () => onVoidBill(row) }
      : null;

  return (
    <DropdownMenuRoot>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="rounded p-1 text-muted transition hover:bg-bg hover:text-ink-2"
          aria-label="Row actions"
        >
          <MoreHorizontal size={16} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild className="flex items-center gap-2">
          <Link to="/procurement/bills/$billId" params={{ billId: row.id }}>
            <Eye size={14} />
            View bill
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem disabled className="flex items-center gap-2">
          <LinkIcon size={14} />
          Match to PO
        </DropdownMenuItem>
        {row.status === "approval_pending" || row.status === "ready_to_pay" ? (
          <DropdownMenuItem disabled className="flex items-center gap-2">
            <Check size={14} />
            Approve
          </DropdownMenuItem>
        ) : null}
        {isEditable ? (
          <DropdownMenuItem asChild className="flex items-center gap-2">
            <Link to="/procurement/bills/$billId/edit" params={{ billId: row.id }}>
              <Pencil size={14} />
              Edit
            </Link>
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem disabled className="flex items-center gap-2">
          <Download size={14} />
          Download bill
        </DropdownMenuItem>
        {row.status !== "paid" && row.status !== "void" && destructive ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={busy === true}
              onSelect={(event) => {
                // `onSelect` closes the menu by default — that's what we
                // want; the host then opens the confirm dialog.
                event.preventDefault();
                destructive.onSelect();
              }}
              className="flex items-center gap-2 text-rose-700 focus:text-rose-700"
            >
              <Trash2 size={14} />
              {destructive.label}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenuRoot>
  );
}

function StatusBadge({
  status,
  match,
}: {
  status: BillStatus;
  match: BillListRow["threeWayMatch"];
}) {
  const tone = statusBadgeTone(status, match);
  return (
    <span
      className={[
        "inline-flex max-w-full items-center rounded-full border px-2.5 py-1 text-xs font-semibold",
        tone,
      ].join(" ")}
    >
      <span className="truncate">{statusLabel(status, match)}</span>
    </span>
  );
}

function statusLabel(status: BillStatus, match: BillListRow["threeWayMatch"]): string {
  if (status === "draft") return "Draft";
  if (status === "review")
    return match === "mismatch" ? "Held · QC review" : "Held · proof pending";
  if (status === "approval_pending") return "Awaiting · founder OTP";
  if (status === "ready_to_pay") return "Awaiting";
  if (status === "paid") return "Paid";
  return "Voided";
}

function billDisplayNumber(row: BillListRow): string {
  const number = row.vendorDocNumber?.trim();
  if (!number || isUuidLike(number)) {
    return row.status === "draft" ? "— draft —" : "— no bill # —";
  }
  return number;
}

function isUuidLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

function statusBadgeTone(status: BillStatus, match: BillListRow["threeWayMatch"]): string {
  if (status === "draft") return "border-line bg-raised text-muted";
  if (status === "review") {
    return match === "mismatch"
      ? "border-rose-200 bg-rose-50 text-rose-700"
      : "border-amber-200 bg-amber-50 text-amber-700";
  }
  if (status === "approval_pending" || status === "ready_to_pay") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  if (status === "paid") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  return "border-line bg-bg text-muted";
}

function dueMeta(row: BillListRow): string {
  if (row.status === "paid") return "paid";
  if (!row.dueOn) return "No due date";
  const days = daysFromToday(row.dueOn);
  if (days < 0) return `${Math.abs(days)}d overdue`;
  if (days === 0) return "due today";
  return `${days}d remaining`;
}

function dueTone(row: BillListRow): string {
  if (row.status === "paid") return "text-emerald-700";
  if (!row.dueOn) return "text-muted";
  const days = daysFromToday(row.dueOn);
  if (days < 0) return "font-semibold text-rose-700";
  if (days <= 7) return "font-semibold text-amber-700";
  return "text-muted";
}

/**
 * App-level wall-clock read for relative-time UI ("3d overdue", "due today").
 * AGENTS.md §7 rule 12 restricts `new Date()` in *domain* code; UI relative-
 * time labels are not part of the financial-truth surface and may read the
 * client clock. Server-truth values come from the tRPC payload.
 */
function daysFromToday(isoDate: string): number {
  const due = new Date(`${isoDate}T00:00:00Z`);
  const today = new Date();
  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return Math.round((due.getTime() - todayUtc) / (1000 * 60 * 60 * 24));
}

function vendorMeta(row: BillListRow): string {
  const parts = [
    row.vendorCity ?? row.vendorState,
    sourceLabel(row.source),
    matchLabel(row.threeWayMatch),
  ].filter(Boolean);
  return parts.join(" · ");
}

function sourceLabel(source: BillListRow["source"]): string {
  if (source === "photo") return "photo upload";
  if (source === "email_pdf") return "email PDF";
  return "manual entry";
}

function matchLabel(match: BillListRow["threeWayMatch"]): string {
  if (match === "matched") return "matched";
  if (match === "mismatch") return "mismatch";
  return "no PO match";
}
